import type {
  RulesetSummaryDto,
  RulesetSource,
  MdExpenseCategoryGroupDto,
  StatutoryElectionDatesDto,
} from '@signalvote/shared';
import { apiJson, apiResponse } from './apiFetch';
import { campaignHeaders, defineQuery } from '../../lib/data';
import { FINANCE_STALE_TIME } from './queryUtils';

/** The rulesets the app ships, for the finance setup and the rules viewer. */
export const useRulesets = defineQuery({
  key: () => ['finance', 'rulesets', 'list'],
  reads: 'GET /api/finance/rulesets',
  fetch: (_: undefined, { campaignId }): Promise<RulesetSummaryDto[]> =>
    apiJson<RulesetSummaryDto[]>('/api/finance/rulesets', {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch rulesets',
    }),
});

/** The election dates the jurisdiction's law fixes for `year`, offered for a blank date. */
export const useStatutoryElectionDates = defineQuery({
  key: ({ code, year }: { code: string; year: number }) => ['finance', 'rulesets', 'statutory-election-dates', code, year],
  reads: 'GET /api/finance/rulesets/{code}/statutory-election-dates',
  staleTime: Infinity,
  fetch: ({ code, year }, { campaignId }): Promise<StatutoryElectionDatesDto> =>
    apiJson<StatutoryElectionDatesDto>(
      `/api/finance/rulesets/${encodeURIComponent(code)}/statutory-election-dates?year=${year}`,
      { headers: campaignHeaders(campaignId), error: 'Failed to fetch statutory election dates' },
    ),
});

export interface RulesetSourceResult {
  /** The file text exactly as served — what the raw view shows. */
  text: string;
  ruleset: RulesetSource;
}

/**
 * The ruleset FILE for the rules viewer — every version, every policy switch, and the
 * `_comment` citations the typed detail endpoint drops. Keeps the served text alongside
 * the parsed shape so the raw view and the sections come from one fetch. Keyed by ruleset
 * code (the file is global reference data), but the endpoint requires Finance.Manage in the
 * current campaign because the notes cite internal issues. Resolves null for an unknown code.
 */
export const useRulesetSource = defineQuery({
  key: (code: string) => ['finance', 'rulesets', 'source', code],
  reads: 'GET /api/finance/rulesets/{code}/source',
  staleTime: FINANCE_STALE_TIME,
  fetch: async (code, { campaignId }): Promise<RulesetSourceResult | null> => {
    const response = await apiResponse(`/api/finance/rulesets/${code}/source`, { headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch ruleset source');
    const text = await response.text();
    return { text, ruleset: JSON.parse(text) as RulesetSource };
  },
});

/**
 * Maryland (MDCRIS, ADR-073) — the coded Expense Category → Purpose cascade driving the searchable,
 * category-filtered Purpose picker on the expenditure form. IS the checked-in
 * `MdExpensePurposeAllowList` (served, never re-transcribed on the client), so the picker can only
 * offer listed pairs. Only read for a Maryland ruleset, so a non-MD form renders no picker.
 */
export const useMdExpensePurposeCascade = defineQuery({
  key: (rulesetCode: string) => ['finance', 'rulesets', 'expense-purposes', rulesetCode],
  reads: 'GET /api/finance/rulesets/{code}/expense-purposes',
  staleTime: FINANCE_STALE_TIME,
  fetch: (rulesetCode, { campaignId }): Promise<MdExpenseCategoryGroupDto[]> =>
    apiJson<MdExpenseCategoryGroupDto[]>(`/api/finance/rulesets/${rulesetCode}/expense-purposes`, {
      headers: campaignHeaders(campaignId),
      error: 'Failed to fetch Maryland expense purposes',
    }),
});
